import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TableSettings from '../../shared/components/TableSettings';
import { EVENT_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/eventsColumnsConfig';
import { buildEventsFilterGroups, STATUS_OPTIONS } from '../constants/eventsFiltersConfig';
import { withRoomFilterGroup } from '../constants/salesFiltersConfig';
import useReportingPage from '../hooks/useReportingPage';

const axios = require('axios').default;
const qs = require('qs');

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, o.label]));

const STATUS_BADGE_CLASS = {
  on_sale: 'badge-success',
  announced: 'badge-info',
  pre_sale: 'badge-primary',
  artist_pre_sale: 'badge-primary',
  confirm: 'badge-secondary',
  hold: 'badge-warning',
  canceled: 'badge-danger',
  past: 'badge-light',
};

function ReportingEventsPage({ team, csrfToken, venueOwnerships, roomOptions }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('this_year');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(50);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortRules, setSortRules] = useState([{ field: 'start_time', direction: 'desc' }]);
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);

  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns());
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [summary, setSummary] = useState({ total_events: 0, total_tickets_sold: 0, total_tickets_scanned: 0, scan_rate: 0 });
  const [priorSummary, setPriorSummary] = useState(null);

  const filterGroups = useMemo(
    () => withRoomFilterGroup(buildEventsFilterGroups(venueOwnerships), roomOptions),
    [venueOwnerships, roomOptions]
  );

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(`reportingEventColumns_${team.id}`);
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const knownIds = new Set(EVENT_COLUMNS.map(c => c.id));
        const validVisible = visible.filter(c => knownIds.has(c.id));
        const validHidden = hidden.filter(c => knownIds.has(c.id));
        const savedIds = new Set([...validVisible.map(c => c.id), ...validHidden.map(c => c.id)]);
        const newColumns = EVENT_COLUMNS.filter(c => !savedIds.has(c.id) && !c.alwaysVisible);
        setVisibleColumns(validVisible);
        setHiddenColumns([...validHidden, ...newColumns]);
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters, sortRules]);

  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      per_page: perPage,
      sort_by: sortRules.map(r => r.field),
      sort_direction: sortRules.map(r => r.direction),
    };

    if (searchTerm) params.search = searchTerm;

    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (key === 'venue_ownership_id' && Array.isArray(value) && value.length === 1) {
        params.venue_ownership_id = value[0];
      }
      if (key === 'calendar_classification' && Array.isArray(value) && value.length > 0) {
        params.calendar_classification = value;
      }
      if (key === 'room_ids' && Array.isArray(value) && value.length > 0) {
        params.room_ids = value;
      }
      if (key === 'age_restriction' && Array.isArray(value) && value.length > 0) {
        params.age_restriction = value;
      }
      if (key === 'ticketing_arrangement' && Array.isArray(value) && value.length > 0) {
        params.ticketing_arrangement = value;
      }
      if (key === 'is_public' && Array.isArray(value) && value.length === 1) {
        params.is_public = value[0];
      }
      if (key === 'tickets_sold' && typeof value === 'object' && value !== null) {
        if (value.min) params.tickets_sold_min = value.min;
        if (value.max) params.tickets_sold_max = value.max;
      }
      if (key === 'tickets_scanned' && typeof value === 'object' && value !== null) {
        if (value.min) params.tickets_scanned_min = value.min;
        if (value.max) params.tickets_scanned_max = value.max;
      }
    });

    return params;
  }, [currentPage, perPage, sortRules, searchTerm, activeFilters, dateRange]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = buildApiParams();

        const priorParams = comparison.start ? {
          ...params,
          page: 1,
          per_page: 1,
          start_date: comparison.start,
          end_date: comparison.end,
        } : null;

        const [currentRes, priorRes] = await Promise.all([
          axios.get(`/teams/${team.id}/reporting/dashboard/events/data.json`, {
            params,
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
          }),
          priorParams ? axios.get(`/teams/${team.id}/reporting/dashboard/events/data.json`, {
            params: priorParams,
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
          }) : Promise.resolve(null),
        ]);

        const data = currentRes.data || {};
        setEvents(data.events || []);
        setTotalEntries(data.pagination?.total_entries || 0);
        setTotalPages(data.pagination?.total_pages || 1);
        if (data.summary) setSummary(data.summary);
        setPriorSummary(priorRes ? (priorRes.data || {}).summary || null : null);
      } catch (e) {
        setError('Failed to load events.');
        toast.error('Failed to load events.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [team && team.id, csrfToken, currentPage, perPage, sortRules, searchTerm, activeFilters, dateRange]);

  const handleColumnSort = (colId) => {
    const col = EVENT_COLUMNS.find(c => c.id === colId);
    if (!col || !col.sortable) return;

    setSortRules(prev => {
      const existing = prev.length === 1 && prev[0].field === colId;
      if (existing) {
        return [{ field: colId, direction: prev[0].direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ field: colId, direction: 'asc' }];
    });
  };

  const sortableColumns = EVENT_COLUMNS.filter(c => c.sortable);

  const handleSortRuleChange = (index, key, value) => {
    setSortRules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const handleAddSortRule = () => {
    const usedFields = new Set(sortRules.map(r => r.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.id));
    if (nextCol) {
      setSortRules(prev => [...prev, { field: nextCol.id, direction: 'asc' }]);
    }
  };

  const handleRemoveSortRule = (index) => {
    setSortRules(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(`reportingEventColumns_${team.id}`, JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  const buildExportUrl = () => {
    const urlParams = new URLSearchParams();
    const apiParams = buildApiParams();
    if (apiParams.search) urlParams.set('search', apiParams.search);
    if (apiParams.venue_ownership_id) urlParams.set('venue_ownership_id', apiParams.venue_ownership_id);
    if (apiParams.start_date) urlParams.set('start_date', apiParams.start_date);
    if (apiParams.end_date) urlParams.set('end_date', apiParams.end_date);
    if (apiParams.sort_by && apiParams.sort_by.length > 0) {
      apiParams.sort_by.forEach(v => urlParams.append('sort_by[]', v));
    }
    if (apiParams.sort_direction && apiParams.sort_direction.length > 0) {
      apiParams.sort_direction.forEach(v => urlParams.append('sort_direction[]', v));
    }
    if (apiParams.is_public) urlParams.set('is_public', apiParams.is_public);
    if (apiParams.tickets_sold_min) urlParams.set('tickets_sold_min', apiParams.tickets_sold_min);
    if (apiParams.tickets_sold_max) urlParams.set('tickets_sold_max', apiParams.tickets_sold_max);
    if (apiParams.tickets_scanned_min) urlParams.set('tickets_scanned_min', apiParams.tickets_scanned_min);
    if (apiParams.tickets_scanned_max) urlParams.set('tickets_scanned_max', apiParams.tickets_scanned_max);
    const arrayParams = ['calendar_classification', 'age_restriction', 'ticketing_arrangement', 'room_ids'];
    arrayParams.forEach(key => {
      const values = apiParams[key];
      if (values && values.length > 0) {
        values.forEach(v => urlParams.append(`${key}[]`, v));
      }
    });
    return `/teams/${team.id}/reporting/dashboard/events/export?${urlParams.toString()}`;
  };

  useEffect(() => {
    if (!overflowMenuOpen && !sortPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (overflowMenuOpen && !e.target.closest('.reporting-overflow-menu')) {
        setOverflowMenuOpen(false);
      }
      if (sortPopoverOpen && !e.target.closest('.reporting-sort-popover')) {
        setSortPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [overflowMenuOpen, sortPopoverOpen]);

  const sortArrow = (colId) => {
    const rule = sortRules.find(r => r.field === colId);
    if (!rule) return null;
    const index = sortRules.indexOf(rule);
    const arrow = rule.direction === 'asc' ? '\u2191' : '\u2193';
    return sortRules.length > 1 ? ` ${index + 1}${arrow}` : ` ${arrow}`;
  };

  const tableColumns = useMemo(() => {
    return visibleColumns.map(col => {
      const colConfig = EVENT_COLUMNS.find(c => c.id === col.id);
      const isSortable = colConfig && colConfig.sortable;

      return {
        key: col.id,
        label: isSortable ? (
          <span
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => handleColumnSort(col.id)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleColumnSort(col.id); }}
          >
            {col.label}{sortArrow(col.id)}
          </span>
        ) : col.label,
      };
    });
  }, [visibleColumns, sortRules]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    });
  };

  const renderCellValue = (col, event) => {
    switch (col.id) {
      case 'title':
        return event.title || '—';
      case 'start_time':
        return <span className="text-nowrap">{formatDate(event.start_time)}</span>;
      case 'end_time':
        return <span className="text-nowrap">{formatDateTime(event.end_time)}</span>;
      case 'calendar_classification': {
        const label = STATUS_LABELS[event.calendar_classification] || event.calendar_classification || '—';
        const badgeClass = STATUS_BADGE_CLASS[event.calendar_classification] || 'badge-secondary';
        return <span className={`badge ${badgeClass}`}>{label}</span>;
      }
      case 'venue_name':
        return event.venue_name || '—';
      case 'headliner':
        return event.headliner || '—';
      case 'promoter_name':
        return event.promoter_name || '—';
      case 'support_acts':
        return (event.support_acts && event.support_acts.length > 0)
          ? event.support_acts.join(', ')
          : '—';
      case 'age_restriction':
        return event.age_restriction || '—';
      case 'ticketing_arrangement':
        return event.ticketing_arrangement || '—';
      case 'is_public':
        return event.is_public ? 'Yes' : 'No';
      case 'presenter':
        return event.presenter || '—';
      case 'tickets_sold':
        return event.tickets_sold != null ? event.tickets_sold.toLocaleString() : '0';
      case 'tickets_scanned':
        return event.tickets_scanned != null ? event.tickets_scanned.toLocaleString() : '0';
      case 'id':
        return event.id;
      default:
        return '—';
    }
  };

  const kpiCards = [
    { top: { label: 'Total Events', value: summary.total_events.toLocaleString(), current: summary.total_events, prior: priorSummary?.total_events } },
    { top: { label: 'Tickets Sold', value: summary.total_tickets_sold.toLocaleString(), current: summary.total_tickets_sold, prior: priorSummary?.total_tickets_sold } },
    { top: { label: 'Tickets Scanned', value: summary.total_tickets_scanned.toLocaleString(), current: summary.total_tickets_scanned, prior: priorSummary?.total_tickets_scanned } },
    { top: { label: 'Scan Rate', value: `${summary.scan_rate}%`, current: summary.scan_rate, prior: priorSummary?.scan_rate } },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="events"
      title="Events"
      subtitle="Browse and filter events across your venues."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Events"
      filterSubtitle="Apply filters to find specific events."
    >
          <KpiCardGrid
            loading={loading}
            isMobile={isMobile}
            columns={4}
            cards={kpiCards}
            comparisonLabel={comparison.label}
          />

          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--sl-text-primary, #101828)' }}>Events</h6>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <a
                  href={buildExportUrl()}
                  className="btn btn-sm d-flex align-items-center"
                  style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32, textDecoration: 'none' }}
                >
                  Export CSV <i className="fas fa-download" style={{ fontSize: 12 }} />
                </a>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-columns mr-2" style={{ fontSize: 12 }} />
                    Table Settings
                    {hiddenColumns.length > 0 && (
                      <span className="ml-1" style={{ background: 'var(--sl-fg-brand, #1982c4)', color: 'var(--sl-text-white, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                        {hiddenColumns.length}
                      </span>
                    )}
                  </button>
                  <TableSettings
                    isOpen={tableSettingsOpen}
                    onClose={() => setTableSettingsOpen(false)}
                    visibleFields={visibleColumns}
                    hiddenFields={hiddenColumns}
                    onFieldsChange={handleColumnsChange}
                  />
                </div>
                <div className="reporting-sort-popover" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-sort mr-2" style={{ fontSize: 12 }} />
                    Sort{sortRules.length > 1 ? ` (${sortRules.length})` : ''}
                  </button>
                  {sortPopoverOpen && (
                    <div
                      className="card shadow-sm"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        left: 'auto',
                        zIndex: 1050,
                        minWidth: isMobile ? 280 : 420,
                        marginTop: 4,
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="font-weight-bold">Sort by</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            onClick={() => setSortPopoverOpen(false)}
                          >
                            <i className="fal fa-times" />
                          </button>
                        </div>
                        {sortRules.map((rule, index) => {
                          const col = EVENT_COLUMNS.find(c => c.id === rule.field);
                          const sortType = col?.sortType || 'text';
                          const dirLabels = SORT_TYPES[sortType] || SORT_TYPES.text;
                          return (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.field}
                                onChange={(e) => handleSortRuleChange(index, 'field', e.target.value)}
                              >
                                {sortableColumns.map(c => (
                                  <option
                                    key={c.id}
                                    value={c.id}
                                    disabled={sortRules.some((r, i) => i !== index && r.field === c.id)}
                                  >
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="form-control form-control-sm"
                                style={{ flex: '1 1 0' }}
                                value={rule.direction}
                                onChange={(e) => handleSortRuleChange(index, 'direction', e.target.value)}
                              >
                                <option value="asc">{dirLabels.asc}</option>
                                <option value="desc">{dirLabels.desc}</option>
                              </select>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => handleRemoveSortRule(index)}
                                disabled={sortRules.length <= 1}
                                style={{ opacity: sortRules.length <= 1 ? 0.3 : 1 }}
                              >
                                <i className="fal fa-times" />
                              </button>
                            </div>
                          );
                        })}
                        {sortRules.length < sortableColumns.length && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-primary"
                            onClick={handleAddSortRule}
                          >
                            <i className="fal fa-plus mr-1" />
                            Add another sort
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ position: 'relative', maxWidth: 400, flex: 1 }}>
                  <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
                  />
                </div>
                <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 500 }}>
                  {totalEntries.toLocaleString()} event{totalEntries !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    {visibleColumns.map((col) => {
                      const colConfig = EVENT_COLUMNS.find(c => c.id === col.id);
                      const isSortable = colConfig && colConfig.sortable;
                      return (
                        <th
                          key={col.id}
                          style={{
                            fontWeight: 500,
                            fontSize: 12,
                            color: 'var(--sl-text-tertiary, #667085)',
                            textTransform: 'uppercase',
                            ...(col.id === 'title' ? { paddingLeft: 24 } : {}),
                            ...(isSortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                          }}
                          onClick={isSortable ? () => handleColumnSort(col.id) : undefined}
                        >
                          {col.label}{sortArrow(col.id)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{error}</td>
                    </tr>
                  ) : events.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center py-4 text-muted">No events found.</td>
                    </tr>
                  ) : events.map((event) => (
                    <tr key={event.id}>
                      {visibleColumns.map(col => (
                        <td key={col.id} style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', ...(col.id === 'title' ? { paddingLeft: 24, fontWeight: 600, color: 'var(--sl-text-primary, #101828)' } : {}) }}>
                          {renderCellValue(col, event)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
                <TeamPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalEntries}
                  pageSize={perPage}
                  itemLabel="events"
                  onPageChange={(page) => {
                    if (!loading && page >= 1 && page <= totalPages) {
                      setCurrentPage(page);
                    }
                  }}
                />
              </div>
            )}
          </div>
    </ReportingPageLayout>
  );
}

export default ReportingEventsPage;
